import React, { useMemo, useState, useEffect, useRef } from 'react';
import api from '../../../../services/api';
import { useAuth } from '../../../../context/AuthContext';
import { getTaskStatusColor } from '../../utils/taskUtils';
import KanbanCard from '../Kanban/KanbanCard';

const toLocalYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getMonthLabel = (date) =>
  date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

const TaskCalendarView = ({
  tasks = [],
  selectedDateRange,
  onChangeDateRange,
  onTaskClick
}) => {
  const { user } = useAuth();
  const GOOGLE_GRANTED_KEY = 'googleCalGranted';
  // Boot logs to verify component mount and env
  useEffect(() => {
    try {
      const base = (api?.defaults?.baseURL || '').replace(/\/+$/,'');
      const storedExpiry = Number(localStorage.getItem('googleCalTokenExpiry') || '0');
      const hasToken = !!localStorage.getItem('googleCalToken');
      console.log('[TaskCalendarView] mounted', {
        apiBaseURL: base || '(empty)',
        hasStoredToken: hasToken,
        tokenExpiresInMs: storedExpiry ? (storedExpiry - Date.now()) : 0
      });
    } catch (e) {
      console.warn('[TaskCalendarView] mount log failed', e);
    }
  }, []);
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  // Responsive day-cell width so calendar + side list fit side-by-side
  // Compute based on the actual width of the calendar-left container
  const leftRef = useRef(null);
  const [dayCellWidth, setDayCellWidth] = useState(120);
  useEffect(() => {
    const GAP = 8; // matches GRID_GAP
    const MIN_CELL = 96;
    const MAX_CELL = 140;
    const measure = () => {
      const el = leftRef.current;
      if (!el) return;
      const available = el.clientWidth;
      if (!available || available <= 0) return;
      const computed = Math.floor((available - GAP * 6) / 7);
      const clamped = Math.max(MIN_CELL, Math.min(MAX_CELL, computed));
      setDayCellWidth(clamped);
    };
    // Initial
    measure();
    // Observe container resize
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      if (leftRef.current) ro.observe(leftRef.current);
    } else {
      const onResize = () => measure();
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }
    return () => {
      if (ro && leftRef.current) ro.disconnect();
    };
  }, []);
  const DAY_CELL_WIDTH = dayCellWidth;
  const DAY_CELL_HEIGHT = 130;
  const GRID_GAP = 8;

  const [selectedDay, setSelectedDay] = useState(null);

  const anchorDate = useMemo(() => {
    const d = selectedDateRange?.startDate ? new Date(selectedDateRange.startDate) : new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, [selectedDateRange]);

  const monthStart = useMemo(() => {
    const d = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [anchorDate]);

  const gridStart = useMemo(() => {
    const d = new Date(monthStart);
    // Start on Sunday for the grid
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    return d;
  }, [monthStart]);

  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [gridStart]);

  const tasksByDay = useMemo(() => {
    const map = {};
    tasks.forEach((t) => {
      if (!t || !t.dueDate) return;
      const d = new Date(t.dueDate);
      const key = toLocalYMD(d);
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return map;
  }, [tasks]);

  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Google Calendar integration state
  const [googleCalToken, setGoogleCalToken] = useState(null);
  const [googleCalTokenExpiry, setGoogleCalTokenExpiry] = useState(0);
  const [isFetchingGoogleCal, setIsFetchingGoogleCal] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [calendarModalData, setCalendarModalData] = useState(null);
  const [showGoogleEvents, setShowGoogleEvents] = useState(true);
  const CAL_EVENT_COLOR = '#10b981';

  // Load stored token on mount
  useEffect(() => {
    try {
      const storedExpiry = Number(localStorage.getItem('googleCalTokenExpiry') || '0');
      const storedToken = localStorage.getItem('googleCalToken') || null;
      if (storedToken && Date.now() < storedExpiry) {
        setGoogleCalToken(storedToken);
        setGoogleCalTokenExpiry(storedExpiry);
        setGoogleConnected(true);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Silent attempt to get token on load only if user previously connected.
  useEffect(() => {
    if (googleConnected) return;
    (async () => {
      try {
        const previouslyGranted = (() => {
          try {
            return localStorage.getItem(GOOGLE_GRANTED_KEY) === '1';
          } catch {
            return false;
          }
        })();
        if (!previouslyGranted) return;
        await requestGoogleCalendarToken(true);
        setGoogleConnected(true);
      } catch (e) {
        // ignore - will connect on user action
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleConnected]);

  const persistToken = (token, expiryMs) => {
    try {
      localStorage.setItem('googleCalToken', token || '');
      localStorage.setItem('googleCalTokenExpiry', String(expiryMs || 0));
    } catch (e) {
      // ignore
    }
  };

  const persistGranted = () => {
    try {
      localStorage.setItem(GOOGLE_GRANTED_KEY, '1');
    } catch {
      // ignore
    }
  };

  const gotoMonth = (offset) => {
    const next = new Date(monthStart.getFullYear(), monthStart.getMonth() + offset, 1);
    const first = new Date(next.getFullYear(), next.getMonth(), 1);
    first.setHours(0, 0, 0, 0);
    const last = new Date(next.getFullYear(), next.getMonth() + 1, 0, 23, 59, 59, 999);
    onChangeDateRange &&
      onChangeDateRange({
        startDate: first.toISOString(),
        endDate: last.toISOString(),
        label: getMonthLabel(first)
      });
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
  };

  const ensureGsiLoaded = () =>
    new Promise((resolve) => {
      if (window.google && window.google.accounts && window.google.accounts.oauth2) return resolve();
      const id = 'gsi-client';
      if (document.getElementById(id)) {
        const check = () => (window.google && window.google.accounts && window.google.accounts.oauth2) ? resolve() : setTimeout(check, 50);
        check();
        return;
      }
      const s = document.createElement('script');
      s.id = id;
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      document.head.appendChild(s);
    });

  const requestGoogleCalendarToken = (silent = false) =>
    new Promise(async (resolve, reject) => {
      try {
        if (!GOOGLE_CLIENT_ID) return reject(new Error('Missing GOOGLE_CLIENT_ID'));
        await ensureGsiLoaded();
        const gis = window.google && window.google.accounts && window.google.accounts.oauth2;
        if (!gis) return reject(new Error('Google OAuth script not loaded'));
        const isTokenValid = googleCalToken && Date.now() < googleCalTokenExpiry;
        const tokenClient = gis.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: 'https://www.googleapis.com/auth/calendar.readonly',
          // Silent when asked; otherwise consent if we don't have a token
          prompt: silent ? '' : (isTokenValid ? '' : 'consent'),
          callback: async (resp) => {
            if (resp && resp.access_token) {
              const expiresInSec = Number(resp.expires_in || 3600);
              setGoogleCalToken(resp.access_token);
              // Treat Google access tokens as short-lived; refresh 60s before expiry.
              const expiry = Date.now() + Math.max(0, (expiresInSec - 60) * 1000);
              setGoogleCalTokenExpiry(expiry);
              setGoogleConnected(true);
              persistToken(resp.access_token, expiry);
              persistGranted();
              console.log('[TaskCalendarView] GIS token acquired', {
                expiresInSec,
                expiry
              });
              // Send to backend to save
              try {
                await api.post('/api/integrations/google/save-access-token', {
                  accessToken: resp.access_token,
                  expiresInSec
                });
              } catch (e) {
                console.warn('[TaskCalendarView] failed to save access token to backend', e);
              }
              resolve(resp.access_token);
            } else {
              console.warn('[TaskCalendarView] GIS token callback with no access_token', resp);
              reject(new Error('Failed to obtain access token'));
            }
          }
        });
        tokenClient.requestAccessToken({ prompt: silent ? '' : undefined });
      } catch (err) {
        console.error('[TaskCalendarView] requestGoogleCalendarToken error', err);
        reject(err);
      }
    });

  const fetchGoogleCalendarForRange = async () => {
    try {
      setIsFetchingGoogleCal(true);
      let token = googleCalToken;
      if (!token || Date.now() >= googleCalTokenExpiry) {
        token = await requestGoogleCalendarToken();
      }
      // Determine selected range (fallback to current month)
      const startISO =
        selectedDateRange?.startDate ||
        new Date(monthStart.getFullYear(), monthStart.getMonth(), 1).toISOString();
      const endISO =
        selectedDateRange?.endDate ||
        new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0, 23, 59, 59, 999).toISOString();

      const params = new URLSearchParams({
        timeMin: startISO,
        timeMax: endISO,
        singleEvents: 'true',
        orderBy: 'startTime',
        maxResults: '2500'
      });
      const resp = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/primary/events?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (resp.status === 401) {
        // Token expired/revoked. Try one silent refresh if user previously granted; otherwise require explicit connect.
        try {
          const refreshed = await requestGoogleCalendarToken(true);
          const retry = await fetch(
            `https://www.googleapis.com/calendar/v3/calendars/primary/events?${params.toString()}`,
            { headers: { Authorization: `Bearer ${refreshed}` } }
          );
          const retryData = await retry.json();
          if (retryData && Array.isArray(retryData.items)) setGoogleEvents(retryData.items);
          else setGoogleEvents([]);
          return;
        } catch (_) {
          setGoogleConnected(false);
          setGoogleEvents([]);
          return;
        }
      }
      const data = await resp.json();
      if (data && Array.isArray(data.items)) {
        setGoogleEvents(data.items);
      } else {
        setGoogleEvents([]);
      }
      console.log('Google Calendar events for range', { startISO, endISO, data });
    } catch (err) {
      console.error('Failed to fetch Google Calendar events', err);
    } finally {
      setIsFetchingGoogleCal(false);
    }
  };

  // Auto-fetch when connected and range/month changes
  useEffect(() => {
    if (!googleConnected) return;
    fetchGoogleCalendarForRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleConnected, monthStart, selectedDateRange?.startDate, selectedDateRange?.endDate]);

  const selectedDayKey = useMemo(() => {
    if (!selectedDay) return null;
    return toLocalYMD(selectedDay);
  }, [selectedDay]);

  const selectedDayTasks = useMemo(() => {
    if (!selectedDayKey) return [];
    return tasksByDay[selectedDayKey] || [];
  }, [selectedDayKey, tasksByDay]);

  // Map Google events by start day
  const getEventStartDate = (event) => {
    const start = event?.start || {};
    if (start.dateTime) return new Date(start.dateTime);
    if (start.date) return new Date(start.date + 'T00:00:00');
    return null;
  };

  const googleEventsByDay = useMemo(() => {
    const map = {};
    (googleEvents || []).forEach((ev) => {
      const d = getEventStartDate(ev);
      if (!d) return;
      const key = toLocalYMD(d);
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    });
    return map;
  }, [googleEvents]);

  const selectedDayEvents = useMemo(() => {
    if (!selectedDay) return [];
    const key = toLocalYMD(selectedDay);
    return googleEventsByDay[key] || [];
  }, [selectedDay, googleEventsByDay]);

  const getTaskStartDate = (task) => {
    try {
      if (!task?.dueDate) return null;
      const d = new Date(task.dueDate);
      return isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  };

  const selectedDayCombined = useMemo(() => {
    if (!selectedDay) return [];
    const key = toLocalYMD(selectedDay);
    const combined = [];
    (tasksByDay[key] || []).forEach((t) => combined.push({ type: 'task', start: getTaskStartDate(t), task: t }));
    if (showGoogleEvents) {
      (googleEventsByDay[key] || []).forEach((ev) => combined.push({ type: 'event', start: getEventStartDate(ev), event: ev }));
    }
    combined.sort((a, b) => {
      const at = a.start ? a.start.getTime() : Number.POSITIVE_INFINITY;
      const bt = b.start ? b.start.getTime() : Number.POSITIVE_INFINITY;
      return at - bt;
    });
    return combined;
  }, [selectedDay, tasksByDay, googleEventsByDay, showGoogleEvents]);
  const formatDateTime12 = (dateObj) => {
    if (!dateObj) return '';
    try {
      return dateObj.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return String(dateObj);
    }
  };

  const getMeetLink = (ev) => {
    const direct = ev?.hangoutLink;
    if (direct) return direct;
    const entryPoints = ev?.conferenceData?.entryPoints || [];
    const video = entryPoints.find((ep) => ep?.entryPointType === 'video' && ep?.uri);
    return video?.uri || null;
  };

  const monthLabel = getMonthLabel(monthStart);

  return (
    <div className="calendar-view-container" style={{ position: 'relative', zIndex: 'auto', maxWidth: '100%' }}>
      <div className="calendar-layout" style={{ display: 'flex', gap: 16, alignItems: 'flex-start', position: 'relative', zIndex: 'auto', flexWrap: 'nowrap', maxWidth: '100%' }}>
        {/* Left: Calendar */}
        <div ref={leftRef} className="calendar-left" style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 'auto', maxWidth: 'calc(100% - 380px)' }}>
          <div className="calendar-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <button className="btn-secondary small" onClick={() => gotoMonth(-1)} aria-label="Previous month">Prev</button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontWeight: 600 }}>{monthLabel}</div>
            </div>
            <button className="btn-secondary small" onClick={() => gotoMonth(1)} aria-label="Next month">Next</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 12, color: '#64748b' }}>Only showing tasks with due date</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* Task status legend */}
              {[
                { label: 'Incoming', color: getTaskStatusColor('incoming') },
                { label: 'Pending', color: getTaskStatusColor('pending') },
                { label: 'In-Progress', color: getTaskStatusColor('in-progress') },
                { label: 'Resolved', color: getTaskStatusColor('resolved') },
                { label: 'Rejected', color: getTaskStatusColor('rejected') },
              ].map((item) => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
                  <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 9999, background: item.color }}></span>
                  <span>{item.label}</span>
                </div>
              ))}
              {/* Google Calendar legend + controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: '#475569', colorScheme: 'light' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: '#ffffff', border: '2px solid #2563eb' }}></span>
                  <span>Google Calendar</span>
                </div>
                {googleConnected ? (
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }} title="Show or hide Google Calendar events">
                    <input
                      type="checkbox"
                      checked={showGoogleEvents}
                      onChange={(e) => setShowGoogleEvents(e.target.checked)}
                      aria-label="Show Google Calendar events"
                      style={{
                        accentColor: '#0f172a',
                        backgroundColor: '#ffffff',
                        border: '1px solid rgba(148, 163, 184, 0.5)',
                        borderRadius: 4,
                        width: 16,
                        height: 16
                      }}
                    />
                    <span>Show events</span>
                  </label>
                ) : (
                  <button
                    className="btn-secondary small"
                    onClick={async () => {
                      await requestGoogleCalendarToken(false).catch((e) =>
                        console.error('[TaskCalendarView] GIS token request failed', e)
                      );
                      await fetchGoogleCalendarForRange();
                      setShowGoogleEvents(true);
                    }}
                    disabled={isFetchingGoogleCal}
                    aria-label="Connect Google Calendar"
                    title="Connect your Google Calendar"
                    style={{
                      background: '#ffffff',
                      color: '#0f172a',
                      border: '1px solid #0f172a'
                    }}
                  >
                    {isFetchingGoogleCal ? 'Fetching…' : 'Connect Google Calendar'}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="calendar-scroll" style={{ overflowX: 'auto', overflowY: 'auto', height: 'calc(100vh - 240px)' }}>
            <div
              className="calendar-grid-header"
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(7, ${DAY_CELL_WIDTH}px)`,
                gap: GRID_GAP,
                marginBottom: 4
              }}
            >
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <div key={d} style={{ fontSize: 12, fontWeight: 600, color: '#64748b', padding: '6px 4px' }}>{d}</div>
              ))}
            </div>

            <div
              className="calendar-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(7, ${DAY_CELL_WIDTH}px)`,
                gap: GRID_GAP,
                paddingBottom: 2
              }}
            >
              {days.map((day) => {
          const key = toLocalYMD(day);
          const dayTasks = tasksByDay[key] || [];
          const dayEvents = showGoogleEvents ? (googleEventsByDay[key] || []) : [];
          const inCurrentMonth = day.getMonth() === monthStart.getMonth();
          const isToday = isSameDay(day, today);
          const isSelected = selectedDay && isSameDay(day, selectedDay);

          return (
            <div
              key={key}
              className="calendar-cell"
              onClick={() => handleDayClick(day)}
              role="button"
              tabIndex={0}
              style={{
                width: DAY_CELL_WIDTH,
                height: DAY_CELL_HEIGHT,
                border: '1px solid rgba(226,232,240,0.8)',
                borderRadius: 8,
                padding: 6,
                background: isToday ? 'rgba(59,130,246,0.06)' : '#fff',
                opacity: inCurrentMonth ? 1 : 0.5,
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                boxShadow: isSelected ? '0 0 0 2px rgba(59,130,246,0.4) inset' : 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>
                  {day.getDate()}
                </div>
                {isToday && (
                  <span style={{ fontSize: 10, color: '#2563eb', fontWeight: 600 }}>Today</span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, overflowY: 'auto' }}>
                {(() => {
                  const combined = [];
                  (dayTasks || []).forEach((t) => combined.push({ type: 'task', start: getTaskStartDate(t), task: t }));
                  (dayEvents || []).forEach((ev) => combined.push({ type: 'event', start: getEventStartDate(ev), event: ev }));
                  combined.sort((a, b) => {
                    const at = a.start ? a.start.getTime() : Number.POSITIVE_INFINITY;
                    const bt = b.start ? b.start.getTime() : Number.POSITIVE_INFINITY;
                    return at - bt;
                  });
                  const visible = combined.slice(0, 3);
                  const remaining = Math.max(0, combined.length - visible.length);
                  return (
                    <>
                      {visible.map((item, idx) => {
                        if (item.type === 'task') {
                          const t = item.task;
                          return (
                            <div
                              key={`t-${t.id}-${idx}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                onTaskClick && onTaskClick(t);
                              }}
                              className="calendar-task-chip"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                borderRadius: 5,
                                padding: '2px 5px',
                                background: getTaskStatusColor(t.status),
                                color: '#fff'
                              }}
                              role="button"
                              tabIndex={0}
                              aria-label={`Task ${t.id}`}
                            >
                              <span style={{ fontSize: 11, fontWeight: 700 }}>#{t.id}</span>
                              <span style={{ fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {t.description || t.customer?.name || 'Task'}
                              </span>
                            </div>
                          );
                        } else {
                          const ev = item.event;
                          const summary = ev.summary || 'Calendar Event';
                          return (
                            <div
                              key={`e-${ev.id}-${idx}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCalendarModalData(ev);
                                setCalendarModalOpen(true);
                              }}
                              className="calendar-event-chip"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                borderRadius: 5,
                                padding: '2px 5px',
                                background: '#ffffff',
                                color: '#2563eb',
                                border: '1px solid #2563eb',
                                cursor: 'pointer'
                              }}
                              role="button"
                              tabIndex={0}
                              aria-label={`Calendar event ${summary}`}
                            >
                              <span style={{ fontSize: 11, fontWeight: 700 }}>GC</span>
                              <span style={{ fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {summary}
                              </span>
                            </div>
                          );
                        }
                      })}
                      {remaining > 0 && (
                        <div style={{ fontSize: 12, color: '#475569' }}>+{remaining} more</div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          );
          })}
            </div>
          </div>
        </div>

        {/* Right: Selected Day Task List */}
        <div
          className="calendar-right"
          style={{
            flex: '0 0 360px',
            maxWidth: 420,
            minWidth: 300,
            border: '1px solid rgba(226,232,240,0.8)',
            borderRadius: 12,
            background: '#fff',
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 240px)',
            position: 'relative',
            zIndex: 'auto'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              {selectedDay
                ? `Tasks for ${selectedDay.toLocaleDateString()}`
                : 'Select a day to view tasks'}
            </div>
            {selectedDay && (
              <div style={{ fontSize: 12, color: '#64748b' }}>
                {selectedDayCombined.length} item{selectedDayCombined.length === 1 ? '' : 's'}
              </div>
            )}
          </div>

          {selectedDay ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto', flex: 1 }}>
              {selectedDayCombined.length > 0 ? (
                selectedDayCombined.map((item, idx) => {
                  if (item.type === 'task') {
                    const t = item.task;
                    return (
                      <KanbanCard key={`daylist-${t.id}-${idx}`} task={t} onClick={() => onTaskClick && onTaskClick(t)} />
                    );
                  }
                  const ev = item.event;
                  const start = item.start;
                  const end = ev?.end?.dateTime ? new Date(ev.end.dateTime) : null;
                  const meetLink = getMeetLink(ev);
                  return (
                    <div
                      key={`calev-${ev.id}-${idx}`}
                      onClick={() => { setCalendarModalData(ev); setCalendarModalOpen(true); }}
                      style={{
                        border: '1px solid #2563eb',
                        borderRadius: 8,
                        padding: 8,
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Open calendar event ${ev.summary || ''}`}
                    >
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{ev.summary || 'Calendar Event'}</div>
                      <div style={{ fontSize: 12, color: '#475569' }}>
                        {formatDateTime12(start)}{end ? ` - ${formatDateTime12(end)}` : ''}
                      </div>
                      {ev.location && (
                        <div style={{ fontSize: 12, color: '#475569' }}>{ev.location}</div>
                      )}
                      {meetLink && (
                        <div style={{ marginTop: 6 }}>
                          <a
                            href={meetLink}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-secondary"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              display: 'inline-block',
                              background: '#ffffff',
                              color: '#0f172a',
                              border: '1px solid #0f172a',
                              textDecoration: 'none'
                            }}
                          >
                            Join meeting
                          </a>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div style={{ fontSize: 12, color: '#64748b' }}>No items for this day</div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#64748b' }}>Click on a day to see its tasks here.</div>
          )}
        </div>
      </div>
      {/* Calendar Event Modal */}
      {calendarModalOpen && calendarModalData && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => { setCalendarModalOpen(false); setCalendarModalData(null); }}
        >
          <div
            className="modal-content"
            style={{
              background: '#fff',
              borderRadius: 12,
              width: 'min(600px, 92vw)',
              maxHeight: '80vh',
              overflowY: 'auto',
              padding: 16,
              boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 9999, background: CAL_EVENT_COLOR }}></span>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{calendarModalData.summary || 'Calendar Event'}</div>
              </div>
              <span
                onClick={() => { setCalendarModalOpen(false); setCalendarModalData(null); }}
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer', color: '#0f172a', fontSize: 12 }}
              >
                Close
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14, color: '#334155' }}>
              {calendarModalData.description && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Description</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{calendarModalData.description}</div>
                </div>
              )}
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>When</div>
                <div>
                  {(() => {
                    const s = getEventStartDate(calendarModalData);
                    const e = calendarModalData?.end?.dateTime ? new Date(calendarModalData.end.dateTime) : null;
                    return `${formatDateTime12(s)}${e ? ` - ${formatDateTime12(e)}` : ''}`;
                  })()}
                </div>
              </div>
              {calendarModalData.location && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Location</div>
                  <div>{calendarModalData.location}</div>
                  <div style={{ marginTop: 8, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                    <iframe
                      title="Event Location Map"
                      src={`https://www.google.com/maps?q=${encodeURIComponent(calendarModalData.location)}&output=embed`}
                      style={{ width: '100%', height: 220, border: '0' }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                </div>
              )}
              {(calendarModalData.attendees || []).length > 0 && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Attendees</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {(calendarModalData.attendees || []).map((a, idx) => {
                      const isSelf = !!a.self;
                      const organizerEmail = calendarModalData?.organizer?.email;
                      const isOrganizer = organizerEmail && a?.email && a.email.toLowerCase() === organizerEmail.toLowerCase();
                      return (
                        <div key={`att-${idx}`} style={{ fontSize: 13, color: '#475569' }}>
                          {a.email}
                          {isSelf ? ' (me)' : ''}
                          {isOrganizer ? ' (organizer)' : ''}
                          {a.responseStatus ? ` (${a.responseStatus})` : ''}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                {(() => {
                  const link = getMeetLink(calendarModalData);
                  if (!link) return null;
                  return (
                    <a
                      href={link}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-secondary"
                      style={{
                        display: 'inline-block',
                        background: '#ffffff',
                        color: '#0f172a',
                        border: '1px solid #0f172a',
                        textDecoration: 'none'
                      }}
                    >
                      Join meeting
                    </a>
                  );
                })()}
                {calendarModalData.htmlLink && (
                  <a
                    href={calendarModalData.htmlLink}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary"
                    style={{
                      display: 'inline-block',
                      background: '#ffffff',
                      color: '#0f172a',
                      border: '1px solid #0f172a',
                      textDecoration: 'none'
                    }}
                  >
                    Open in Google Calendar
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskCalendarView;


